import React from 'react';
import { SectionCard } from '../../components/sections/SectionCard';
import { PanelTable } from '../../components/display/PanelTable';
import { AppStatusBadge } from '@shared/components/ui/status-badge/AppStatusBadge';
import type { PanelTableColumn } from '../../components/display/PanelTable/types';
import { INVOICE_DATA, type InvoiceData } from '@shared/data';

interface InvoicesSectionProps {
  isDetailView?: boolean;
  className?: string;
}

const renderAmount = (value: unknown) => {
  const amount = typeof value === 'string' ? value : '$0.00';
  return (
    <span className="font-medium">
      {amount}
    </span>
  );
};

const renderStatus = (_: unknown, item: InvoiceData) => (
  <div className="whitespace-nowrap">
    <AppStatusBadge 
      entityType="invoice" 
      status={item.status}
      size="sm"
    />
  </div>
);

export const InvoicesSection = React.memo<InvoicesSectionProps>(({ isDetailView = false, className }) => {
  const columns: PanelTableColumn<InvoiceData>[] = [
    {
      key: 'invoiceNumber',
      label: 'INVOICE #',
      headerClassName: 'w-[120px]',
      cellClassName: 'font-mono text-xs'
    },
    {
      key: 'description',
      label: 'DESCRIPTION',
      headerClassName: 'w-[180px]',
      cellClassName: 'text-muted-foreground truncate max-w-[180px]'
    },
    {
      key: 'amount',
      label: 'AMOUNT',
      headerClassName: 'w-[100px]',
      render: renderAmount
    },
    {
      key: 'status',
      label: 'STATUS',
      headerClassName: 'w-[90px]',
      render: renderStatus
    },
    {
      key: 'items',
      label: 'ITEMS',
      headerClassName: 'w-[70px]',
      cellClassName: 'text-center'
    },
    {
      key: 'due',
      label: 'DUE',
      headerClassName: 'w-[90px]',
      cellClassName: 'whitespace-nowrap'
    },
    {
      key: 'created',
      label: 'CREATED',
      headerClassName: 'w-[90px]',
      cellClassName: 'whitespace-nowrap text-muted-foreground'
    }
  ];

  const rowActions = (item: InvoiceData) => {
    const baseActions = [
      {
        key: 'view-invoice',
        label: 'View invoice',
        onClick: () => console.log('View invoice', item)
      },
      {
        key: 'duplicate-invoice',
        label: 'Duplicate invoice',
        onClick: () => console.log('Duplicate invoice', item)
      },
      {
        key: 'download-pdf',
        label: 'Download PDF',
        onClick: () => console.log('Download PDF', item)
      },
      {
        key: 'view-subscription',
        label: 'View subscription',
        onClick: () => console.log('View subscription', item)
      }
    ];

    // Add status-specific actions
    if (item.status === 'open' || item.status === 'overdue') {
      baseActions.splice(2, 0, {
        key: 'mark-paid',
        label: 'Mark as paid',
        onClick: () => console.log('Mark as paid', item)
      });
      baseActions.splice(3, 0, {
        key: 'void-invoice',
        label: 'Void invoice',
        onClick: () => console.log('Void invoice', item)
      });
      baseActions.splice(4, 0, {
        key: 'delete-invoice',
        label: 'Delete invoice',
        onClick: () => console.log('Delete invoice', item)
      });
    }

    return baseActions;
  };

  return (
    <SectionCard title="Invoices" layout="down" className={className}>
      <PanelTable
        data={INVOICE_DATA}
        columns={columns}
        variant={isDetailView ? 'full' : 'panel'}
        emptyMessage="No invoices found"
        pageSize={5}
        rowActions={rowActions}
      />
    </SectionCard>
  );
});

InvoicesSection.displayName = 'InvoicesSection';